// =============================================================================
// pages/Customers.jsx  —  CA Business Customer Management
//
// Features:
//   • License summary bar
//   • Unlinked sources banner
//   • Customer list as TABLE: Name | Email | Phone | PAN | GSTIN | Companies | Actions
//   • Add customer modal — email + phone MANDATORY
//   • Edit customer modal — update name, email, phone, GSTIN, notes
//   • Link Tally company modal — "New Customer" section collects email+phone (mandatory)
//   • Expand row → linked Tally companies with unlink
//   • Delete with confirmation
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { PageWrap, PageHeader, Card, Skeleton, useIsMobile } from '../components/UI';
import '../styles/Customers.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ── Auth ──────────────────────────────────────────────────────────────────────
function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (saved) {
      const parsed  = JSON.parse(saved);
      const account = parsed?.account;
      const email   = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      const viewMode = localStorage.getItem("auditpros_view_mode") || "ca";
      const headers = { "Content-Type": "application/json" };
      if (email) headers["x-user-email"] = email;
      headers["x-view-mode"] = viewMode;
      return headers;
    }
  } catch { /* ignore */ }
  return { "Content-Type": "application/json" };
}

// ── Validators ────────────────────────────────────────────────────────────────
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
function validatePAN(pan) {
  if (!pan) return 'PAN is required';
  if (!PAN_REGEX.test(pan.trim().toUpperCase())) return 'Invalid PAN (expected: AAAAA9999A)';
  return null;
}
function validateContact(email, mobile) {
  if (!email?.trim()) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Invalid email address';
  if (!mobile?.trim()) return 'Mobile number is required';
  if (mobile.trim().replace(/\D/g, '').length < 7) return 'Mobile number is too short';
  return null;
}

// ── Date formatter ────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

const LICENSE_BADGE_CFG = {
  active:    { label: 'Active',    color: 'var(--green)',  bg: 'var(--green-bg)'          },
  suspended: { label: 'Suspended', color: 'var(--amber)',  bg: 'var(--amber-bg)'          },
  cancelled: { label: 'Cancelled', color: 'var(--text2)',  bg: 'rgba(139,149,168,0.1)'    },
};
function LicenseBadge({ status }) {
  const c = LICENSE_BADGE_CFG[status] || { label: status, color: 'var(--text2)', bg: 'rgba(139,149,168,0.1)' };
  return (
    <span className="cust-license-badge" style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}33` }}>
      {c.label}
    </span>
  );
}

// ── Parse stored mobile into country code + number ────────────────────────────
const COUNTRY_CODES = [
  { code: '+91',  flag: '🇮🇳' },
  { code: '+1',   flag: '🇺🇸' },
  { code: '+44',  flag: '🇬🇧' },
  { code: '+61',  flag: '🇦🇺' },
  { code: '+971', flag: '🇦🇪' },
  { code: '+65',  flag: '🇸🇬' },
];
function parseMobile(mobile) {
  if (!mobile) return { country_code: '+91', number: '' };
  for (const c of COUNTRY_CODES) {
    if (mobile.startsWith(c.code)) return { country_code: c.code, number: mobile.slice(c.code.length) };
  }
  return { country_code: '+91', number: mobile };
}

const REQ = <span className="cust-req-star">*</span>;

// ── All audit pages — single source of truth for visibility toggles ────────────
const ALL_PAGES = [
  { section: 'Audit',      items: [
    { id: 'overview',  label: 'Audit Dashboard'   },
    { id: 'report',    label: 'Audit Report'       },
    { id: 'audit',     label: 'Audit Issues'       },
  ]},
  { section: 'Compliance', items: [
    { id: 'statutory', label: 'Statutory Check'    },
    { id: 'unbooked',  label: 'Unbooked Payments'  },
    { id: 'balance',   label: 'Balance Check'      },
  ]},
  { section: 'Financials', items: [
    { id: 'receivables', label: 'Receivables'      },
    { id: 'payables',    label: 'Payables'         },
    { id: 'trial',       label: 'Trial Balance'    },
  ]},
  { section: 'Registers',  items: [
    { id: 'sales',     label: 'Sales Register'     },
    { id: 'purchase',  label: 'Purchase Register'  },
    { id: 'gst',       label: 'GST Invoices'       },
    { id: 'bank',      label: 'Bank Book'          },
  ]},
  { section: 'History',    items: [
    { id: 'sync',      label: 'Sync History'       },
  ]},
];
const ALL_PAGE_IDS = ALL_PAGES.flatMap(s => s.items.map(i => i.id));

// ── VisiblePagesEditor — shared toggle grid for default panel + edit modal ─────
function VisiblePagesEditor({ pages, onChange, compact = false }) {
  const isAll    = pages === null;
  const selected = isAll ? new Set(ALL_PAGE_IDS) : new Set(pages || []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    const arr = ALL_PAGE_IDS.filter(i => next.has(i));
    onChange(arr.length === ALL_PAGE_IDS.length ? null : arr);
  };

  const toggleAll = () => onChange(isAll ? [] : null);
  const allChecked = isAll || selected.size === ALL_PAGE_IDS.length;

  return (
    <div>
      <div className="cust-pages-header">
        <span className="cust-pages-section-label">Pages visible to customer</span>
        <button onClick={toggleAll} className="cust-pages-toggle-all">
          {allChecked ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      {ALL_PAGES.map(group => (
        <div key={group.section} className={compact ? 'cust-pages-group cust-pages-group--compact' : 'cust-pages-group'}>
          <div className="cust-pages-group-label">{group.section}</div>
          <div className="cust-pages-items">
            {group.items.map(item => {
              const checked = selected.has(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => toggle(item.id)}
                  className={checked ? 'cust-pill cust-pill--active' : 'cust-pill'}
                >
                  {checked ? '✓ ' : ''}{item.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Mobile input row ──────────────────────────────────────────────────────────
function MobileInput({ countryCode, mobile, onCodeChange, onMobileChange }) {
  return (
    <div className="cust-mobile-input-row">
      <select value={countryCode} onChange={e => onCodeChange(e.target.value)}
        className="cust-input cust-input--cc">
        {COUNTRY_CODES.map(c => (
          <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
        ))}
      </select>
      <input
        type="tel" value={mobile} maxLength={10} placeholder="9876543210"
        onChange={e => onMobileChange(e.target.value.replace(/\D/g, ''))}
        className="cust-input cust-input--mono"
      />
    </div>
  );
}

// ── Shared contact fields block (email + mobile) ──────────────────────────────
function ContactFields({ email, countryCode, mobile, onChange, mandatory = true }) {
  return (
    <>
      <div>
        <label className="cust-label">Email {mandatory ? REQ : <span className="cust-opt-text">(optional)</span>}</label>
        <input type="email" value={email} placeholder="owner@example.com"
          onChange={e => onChange('email', e.target.value)} className="cust-input" />
      </div>
      <div>
        <label className="cust-label">Mobile {mandatory ? REQ : <span className="cust-opt-text">(optional)</span>}</label>
        <MobileInput
          countryCode={countryCode} mobile={mobile}
          onCodeChange={v => onChange('country_code', v)}
          onMobileChange={v => onChange('mobile', v)}
        />
      </div>
    </>
  );
}

// ── Error box ─────────────────────────────────────────────────────────────────
function ErrorBox({ msg }) {
  if (!msg) return null;
  return <div className="cust-error-box">{msg}</div>;
}

// ── Modal shell ───────────────────────────────────────────────────────────────
function Modal({ title, subtitle, onClose, children, maxWidth = 500 }) {
  return (
    <div className="cust-modal-overlay">
      <div className="cust-modal" style={{ maxWidth }}>
        <div className="cust-modal-header">
          <div>
            <div className="cust-modal-title">{title}</div>
            {subtitle && <div className="cust-modal-subtitle">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="cust-modal-close">✕</button>
        </div>
        <div className="cust-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Modal footer buttons ──────────────────────────────────────────────────────
function ModalFooter({ onCancel, onSave, saving, saveLabel = 'Save →' }) {
  return (
    <div className="cust-modal-footer">
      <button onClick={onCancel} className="cust-btn-cancel">Cancel</button>
      <button onClick={onSave} disabled={saving} className="cust-btn-save">
        {saving ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}

// =============================================================================
// Add Customer Modal
// =============================================================================
function AddCustomerModal({ onClose, onSaved }) {
  const [mode, setMode] = useState('collaborative'); // 'collaborative' | 'managed'
  const [form, setForm] = useState({
    pan_number: '', business_name: '', gstin: '',
    email: '', country_code: '+91', mobile: '',
    notes: '', contact_name: '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); setError(''); }

  async function handleSave() {
    const pan = form.pan_number.trim().toUpperCase();
    if (mode === 'collaborative') {
      const panErr = validatePAN(pan);
      if (panErr) return setError(panErr);
      const contactErr = validateContact(form.email, form.mobile);
      if (contactErr) return setError(contactErr);
    } else {
      if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return setError('Invalid PAN format (expected: AAAAA9999A)');
    }
    if (!form.business_name.trim()) return setError('Business name is required');
    if (form.gstin) {
      const g = form.gstin.trim().toUpperCase();
      if (g.length !== 15) return setError('GSTIN must be 15 characters');
      if (pan && g.substring(2, 12) !== pan) return setError(`GSTIN does not match PAN. PAN in GSTIN: ${g.substring(2, 12)}`);
    }
    setSaving(true); setError('');
    try {
      const body = {
        business_name:   form.business_name.trim(),
        engagement_mode: mode,
        gstin:           form.gstin.trim().toUpperCase() || undefined,
        notes:           form.notes.trim() || undefined,
      };
      if (pan) body.pan_number = pan;
      if (mode === 'collaborative') {
        body.email  = form.email.trim();
        body.mobile = `${form.country_code}${form.mobile.trim()}`;
      } else {
        if (form.email.trim())  body.email  = form.email.trim();
        if (form.mobile.trim()) body.mobile = `${form.country_code}${form.mobile.trim()}`;
        if (form.contact_name.trim()) body.contact_name = form.contact_name.trim();
      }
      const res = await fetch(`${BASE}/customers`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to add customer');
      onSaved(data);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const isManaged = mode === 'managed';

  return (
    <Modal title="Add Business Customer" subtitle="Set up a new client engagement" onClose={onClose}>
      {/* Engagement mode toggle */}
      <div className="cust-field-group">
        <label className="cust-label">Engagement Mode</label>
        <div className="cust-toggle-wrap">
          <button
            type="button"
            onClick={() => { setMode('collaborative'); setError(''); }}
            className={!isManaged ? 'cust-toggle-btn cust-toggle-btn--active' : 'cust-toggle-btn'}
          >
            Collaborative
          </button>
          <button
            type="button"
            onClick={() => { setMode('managed'); setError(''); }}
            className={isManaged ? 'cust-toggle-btn cust-toggle-btn--active' : 'cust-toggle-btn'}
          >
            Managed
          </button>
        </div>
        <div className="cust-input-hint">
          {isManaged
            ? 'Managed: you sync and audit on their behalf. They can claim access later.'
            : 'Collaborative: client can log in and view their own audit data.'}
        </div>
      </div>

      <div>
        <label className="cust-label">Business Name {REQ}</label>
        <input type="text" value={form.business_name}
          onChange={e => update('business_name', e.target.value)}
          placeholder="e.g. Neelkanth Phytochem Impex Pvt Ltd"
          className="cust-input"
        />
      </div>

      <div>
        <label className="cust-label">
          PAN Number {!isManaged ? REQ : <span className="cust-opt-text">(optional for managed)</span>}
        </label>
        <input type="text" maxLength={10} value={form.pan_number}
          onChange={e => update('pan_number', e.target.value.toUpperCase())}
          placeholder="AAAAA9999A"
          className="cust-input cust-input--pan"
        />
        <div className="cust-input-hint">Format: 5 letters · 4 digits · 1 letter</div>
      </div>

      {isManaged ? (
        <>
          <div>
            <label className="cust-label">Contact Name <span className="cust-opt-text">(optional)</span></label>
            <input type="text" value={form.contact_name}
              onChange={e => update('contact_name', e.target.value)}
              placeholder="e.g. Ramesh Shah"
              className="cust-input"
            />
          </div>
          <ContactFields
            email={form.email} countryCode={form.country_code} mobile={form.mobile}
            onChange={update} mandatory={false}
          />
        </>
      ) : (
        <ContactFields
          email={form.email} countryCode={form.country_code} mobile={form.mobile}
          onChange={update} mandatory={true}
        />
      )}

      <div>
        <label className="cust-label">GSTIN <span className="cust-opt-text">(optional — auto-validates against PAN)</span></label>
        <input type="text" maxLength={15} value={form.gstin}
          onChange={e => update('gstin', e.target.value.toUpperCase())}
          placeholder="27AABCP1234C1Z5"
          className="cust-input cust-input--mono"
        />
      </div>
      <div>
        <label className="cust-label">Notes <span className="cust-opt-text">(optional, internal)</span></label>
        <textarea value={form.notes} onChange={e => update('notes', e.target.value)}
          placeholder="e.g. GST client since 2022, audit for FY25"
          rows={2} className="cust-input cust-input--textarea"
        />
      </div>
      <ErrorBox msg={error} />
      <ModalFooter onCancel={onClose} onSave={handleSave} saving={saving} saveLabel="Add Customer →" />
    </Modal>
  );
}

// =============================================================================
// Edit Customer Modal
// =============================================================================
function EditCustomerModal({ customer, onClose, onSaved }) {
  const parsed = parseMobile(customer.mobile);
  const [form, setForm] = useState({
    business_name: customer.business_name || '',
    gstin:         customer.gstin || '',
    email:         customer.email || '',
    country_code:  parsed.country_code,
    mobile:        parsed.number,
    notes:         customer.notes || '',
  });
  const [visiblePages,      setVisiblePages]      = useState(customer.visible_pages ?? null);
  const [savingPages,       setSavingPages]       = useState(false);
  const [pagesSaved,        setPagesSaved]        = useState(false);
  const [pagesError,        setPagesError]        = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); setError(''); }

  async function handleSave() {
    if (!form.business_name.trim()) return setError('Business name is required');
    const contactErr = validateContact(form.email, form.mobile);
    if (contactErr) return setError(contactErr);
    if (form.gstin) {
      const g = form.gstin.trim().toUpperCase();
      if (g.length !== 15) return setError('GSTIN must be 15 characters');
      const pan = customer.pan_number;
      if (g.substring(2, 12) !== pan) return setError(`GSTIN does not match PAN. PAN in GSTIN: ${g.substring(2, 12)}`);
    }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${BASE}/customers/${customer.id}`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({
          business_name: form.business_name.trim(),
          gstin:         form.gstin.trim().toUpperCase() || null,
          email:         form.email.trim() || null,
          mobile:        form.mobile.trim() ? `${form.country_code}${form.mobile.trim()}` : null,
          notes:         form.notes.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to update customer');
      onSaved(data);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleSavePages() {
    setSavingPages(true); setPagesError(''); setPagesSaved(false);
    try {
      const res = await fetch(`${BASE}/customers/${customer.id}/visible-pages`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({ pages: visiblePages }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setPagesSaved(true);
      setTimeout(() => setPagesSaved(false), 2000);
    } catch (e) { setPagesError(e.message); }
    finally { setSavingPages(false); }
  }

  return (
    <Modal title="Edit Customer" subtitle={`PAN: ${customer.pan_number} — cannot be changed`} onClose={onClose} maxWidth={560}>
      <div>
        <label className="cust-label">Business Name {REQ}</label>
        <input type="text" value={form.business_name}
          onChange={e => update('business_name', e.target.value)}
          className="cust-input"
        />
      </div>
      <ContactFields
        email={form.email} countryCode={form.country_code} mobile={form.mobile}
        onChange={update} mandatory={true}
      />
      <div>
        <label className="cust-label">GSTIN <span className="cust-opt-text">(optional)</span></label>
        <input type="text" maxLength={15} value={form.gstin}
          onChange={e => update('gstin', e.target.value.toUpperCase())}
          placeholder="27AABCP1234C1Z5"
          className="cust-input cust-input--mono"
        />
      </div>
      <div>
        <label className="cust-label">Notes <span className="cust-opt-text">(optional)</span></label>
        <textarea value={form.notes} onChange={e => update('notes', e.target.value)}
          rows={2} className="cust-input cust-input--textarea"
        />
      </div>
      <ErrorBox msg={error} />
      <ModalFooter onCancel={onClose} onSave={handleSave} saving={saving} saveLabel="Save Changes →" />

      {/* ── Customer-level visible pages override ── */}
      <div className="cust-pages-override">
        <div className="cust-pages-override-header">
          <span className="cust-pages-override-title">Customer Visibility Override</span>
          {visiblePages === null && (
            <span className="cust-badge-default">Using CA default</span>
          )}
        </div>
        <p className="cust-pages-override-desc">
          Override which pages this specific customer can see. Leave all selected to use your CA-level default.
        </p>
        <VisiblePagesEditor pages={visiblePages} onChange={setVisiblePages} compact />
        <div className="cust-pages-override-footer">
          <button onClick={handleSavePages} disabled={savingPages} className="cust-btn-save-visibility">
            {savingPages ? 'Saving…' : 'Save Visibility'}
          </button>
          {visiblePages !== null && (
            <button onClick={() => setVisiblePages(null)} className="cust-btn-reset-visibility">
              Reset to CA default
            </button>
          )}
          {pagesSaved && <span className="cust-status-saved">✓ Saved</span>}
          {pagesError && <span className="cust-status-error">{pagesError}</span>}
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// Link Source Modal
// =============================================================================
function LinkSourceModal({ source, availableSources = [], customers, onClose, onLinked }) {
  const [selectedSourceId, setSelectedSourceId] = useState(source?.id || '');
  const [mode, setMode]             = useState(customers.length > 0 ? 'existing' : 'new');
  const [customerId, setCustomerId] = useState(customers.length === 1 ? customers[0].id : '');
  const [newForm, setNewForm]       = useState({
    pan_number: '', business_name: '', gstin: '',
    email: '', country_code: '+91', mobile: '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  function updateNew(field, value) { setNewForm(f => ({ ...f, [field]: value })); setError(''); }

  const sourcesToPick = availableSources.length > 0 ? availableSources : (source ? [source] : []);

  async function handleLink() {
    if (!selectedSourceId) return setError('Please select a Tally company');
    setSaving(true); setError('');
    try {
      let url, body;
      if (mode === 'existing') {
        if (!customerId) { setSaving(false); return setError('Please select a customer'); }
        url  = `${BASE}/customers/sources/link`;
        body = { source_id: selectedSourceId, business_customer_id: customerId };
      } else {
        const pan = newForm.pan_number.trim().toUpperCase();
        const panErr = validatePAN(pan);
        if (panErr) { setSaving(false); return setError(panErr); }
        if (!newForm.business_name.trim()) { setSaving(false); return setError('Business name is required'); }
        const contactErr = validateContact(newForm.email, newForm.mobile);
        if (contactErr) { setSaving(false); return setError(contactErr); }
        url  = `${BASE}/customers/sources/link-new`;
        body = {
          source_id:     selectedSourceId,
          pan_number:    pan,
          business_name: newForm.business_name.trim(),
          gstin:         newForm.gstin.trim().toUpperCase() || undefined,
          email:         newForm.email.trim(),
          mobile:        `${newForm.country_code}${newForm.mobile.trim()}`,
        };
      }
      const res = await fetch(url, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to link');
      onLinked();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal
      title="Assign Tally Company"
      subtitle={source ? source.name : 'Select a Tally company to assign'}
      onClose={onClose}
      maxWidth={480}
    >
      {(availableSources.length > 0 || !source) && (
        <div>
          <label className="cust-label">Tally Company {REQ}</label>
          {sourcesToPick.length === 0 ? (
            <div className="cust-no-companies">No unlinked Tally companies available.</div>
          ) : (
            <select value={selectedSourceId} onChange={e => setSelectedSourceId(e.target.value)} className="cust-input">
              <option value="">Select a Tally company…</option>
              {sourcesToPick.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      )}

      <div className="cust-toggle-wrap">
        <button
          onClick={() => { setMode('existing'); setError(''); }}
          className={mode === 'existing' ? 'cust-toggle-btn cust-toggle-btn--active' : 'cust-toggle-btn'}
        >Existing Customer</button>
        <button
          onClick={() => { setMode('new'); setError(''); }}
          className={mode === 'new' ? 'cust-toggle-btn cust-toggle-btn--active' : 'cust-toggle-btn'}
        >New Customer</button>
      </div>

      {mode === 'existing' ? (
        <div>
          <label className="cust-label">Select Customer</label>
          {customers.length === 0 ? (
            <div className="cust-no-companies">No customers yet. Switch to "New Customer".</div>
          ) : customers.length === 1 ? (
            <div className="cust-single-customer">
              {customers[0].business_name} ({customers[0].pan_number})
            </div>
          ) : (
            <select value={customerId} onChange={e => setCustomerId(e.target.value)} className="cust-input">
              <option value="">Choose a customer…</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.business_name} ({c.pan_number})</option>)}
            </select>
          )}
        </div>
      ) : (
        <>
          <div>
            <label className="cust-label">PAN Number {REQ}</label>
            <input type="text" maxLength={10} value={newForm.pan_number}
              onChange={e => updateNew('pan_number', e.target.value.toUpperCase())}
              placeholder="AAAAA9999A"
              className="cust-input cust-input--pan"
            />
          </div>
          <div>
            <label className="cust-label">Business Name {REQ}</label>
            <input type="text" value={newForm.business_name}
              onChange={e => updateNew('business_name', e.target.value)}
              placeholder="e.g. ABC Pvt Ltd" className="cust-input"
            />
          </div>
          <ContactFields
            email={newForm.email} countryCode={newForm.country_code} mobile={newForm.mobile}
            onChange={updateNew} mandatory={true}
          />
          <div>
            <label className="cust-label">GSTIN <span className="cust-opt-text">(optional)</span></label>
            <input type="text" maxLength={15} value={newForm.gstin}
              onChange={e => updateNew('gstin', e.target.value.toUpperCase())}
              placeholder="27AABCP1234C1Z5"
              className="cust-input cust-input--mono"
            />
          </div>
        </>
      )}

      <ErrorBox msg={error} />
      <ModalFooter onCancel={onClose} onSave={handleLink} saving={saving} saveLabel="Assign Company →" />
    </Modal>
  );
}

// =============================================================================
// Expanded Row — Linked Tally Companies
// =============================================================================
function ExpandedRow({ customer, allSources, colSpan, onCustomerUpdated, onDelete, onEdit }) {
  const [detail,        setDetail]        = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [deleteError,   setDeleteError]   = useState('');
  const [linking,       setLinking]       = useState(false);
  const [inviting,      setInviting]      = useState(false);
  const [inviteMsg,     setInviteMsg]     = useState('');

  useEffect(() => {
    if (detail !== null) return;
    setLoadingDetail(true);
    fetch(`${BASE}/customers/${customer.id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDetail(d); })
      .finally(() => setLoadingDetail(false));
  }, [customer.id, detail]);

  const handleUnlink = async (sourceId) => {
    const res = await fetch(`${BASE}/customers/sources/link/${sourceId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (res.ok) {
      setDetail(null);
      onCustomerUpdated();
    }
  };

  const handleDelete = async () => {
    setDeleting(true); setDeleteError('');
    try {
      const res = await fetch(`${BASE}/customers/${customer.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Failed to delete');
      onDelete(customer.id);
    } catch (e) { setDeleteError(e.message); setDeleting(false); }
  };

  const linkedSourceIds = new Set((detail?.tallyCompanies || []).map(tc => tc.source_id));
  const availableToLink = allSources.filter(s => !linkedSourceIds.has(s.id));

  return (
    <tr>
      <td colSpan={colSpan} className="cust-expanded-cell">
        <div className="cust-expanded-body">

          {customer.notes && (
            <div className="cust-notes-text">📝 {customer.notes}</div>
          )}

          <div>
            <div className="cust-companies-header">
              <div className="cust-companies-label">
                Linked Tally Companies ({detail?.tallyCompanies?.length ?? '…'})
              </div>
              {availableToLink.length > 0 && (
                <button onClick={() => setLinking(true)} className="cust-btn-link-company">+ Link Company</button>
              )}
            </div>

            {loadingDetail ? (
              <Skeleton rows={2} />
            ) : (!detail?.tallyCompanies || detail.tallyCompanies.length === 0) ? (
              <div className="cust-no-companies">
                No Tally companies linked yet.
                {availableToLink.length > 0 && ' Click "+ Link Company" to assign one.'}
              </div>
            ) : (
              <div className="cust-company-list">
                {detail.tallyCompanies.map(tc => (
                  <div key={tc.source_id} className="cust-company-row">
                    <div className="cust-company-info">
                      <div className="cust-company-name">🏢 {tc.tally_company_name}</div>
                      <div className="cust-company-meta">
                        {tc.last_synced_at && <span>Last sync: {fmtDate(tc.last_synced_at)}</span>}
                        {tc.period_from && <span>Period: {tc.period_from}–{tc.period_to}</span>}
                        <span>Resolved: {tc.resolved_by}</span>
                      </div>
                    </div>
                    <div className="cust-company-actions">
                      {tc.license_status && <LicenseBadge status={tc.license_status} />}
                      <button onClick={() => handleUnlink(tc.source_id)} className="cust-btn-unlink">Unlink</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="cust-action-row">
            <button onClick={onEdit} className="cust-btn-edit-customer">✏ Edit Customer</button>
            {customer.engagement_mode === 'managed' && !customer.claimed_by_user_id && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!customer.email) { setInviteMsg('No email on file — edit the customer to add one first.'); return; }
                  setInviting(true); setInviteMsg('');
                  try {
                    const res = await fetch(`${BASE}/customers/${customer.id}/invite`, {
                      method: 'POST', headers: getAuthHeaders(), body: '{}',
                    });
                    const d = await res.json();
                    if (!res.ok) throw new Error(d.error || 'Failed');
                    setInviteMsg(`Invite sent to ${d.email}`);
                  } catch (err) { setInviteMsg(`Error: ${err.message}`); }
                  finally { setInviting(false); }
                }}
                disabled={inviting}
                className="cust-btn-invite"
              >
                {inviting ? 'Sending…' : '✉ Invite to Collaborate'}
              </button>
            )}
            {customer.engagement_mode === 'managed' && customer.claimed_by_user_id && (
              <span className="cust-joined-badge">
                ✓ Client joined
              </span>
            )}
            {inviteMsg && (
              <span style={{ fontSize: 12, color: inviteMsg.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>
                {inviteMsg}
              </span>
            )}
            <div className="cust-spacer" />
            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)} className="cust-btn-remove">Remove Customer</button>
            ) : (
              <div className="cust-confirm-wrap">
                {deleteError && <div className="cust-delete-error">{deleteError}</div>}
                <div className="cust-confirm-buttons">
                  <button onClick={() => { setConfirmDelete(false); setDeleteError(''); }} className="cust-btn-cancel-small">Cancel</button>
                  <button onClick={handleDelete} disabled={deleting} className="cust-btn-confirm-delete">
                    {deleting ? 'Removing…' : 'Confirm Remove'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {linking && (
          <LinkSourceModal
            source={null}
            availableSources={availableToLink}
            customers={[customer]}
            onClose={() => setLinking(false)}
            onLinked={() => { setLinking(false); setDetail(null); onCustomerUpdated(); }}
          />
        )}
      </td>
    </tr>
  );
}

// =============================================================================
// Customer Table
// =============================================================================
function CustomerTable({ customers, allSources, onDelete, onCustomerUpdated }) {
  const [expandedId, setExpandedId] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);

  const toggle = (id) => setExpandedId(prev => prev === id ? null : id);

  const COL_COUNT = 7;

  return (
    <>
      <div className="cust-table-wrap">
        <table className="cust-table">
          <thead>
            <tr>
              {['Customer Name', 'Email', 'Phone', 'PAN', 'GSTIN', 'Companies', ''].map((h, i) => (
                <th key={i} className="cust-th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {customers.map(c => {
              const isExpanded = expandedId === c.id;
              return (
                <React.Fragment key={c.id}>
                  <tr
                    onClick={() => toggle(c.id)}
                    className={isExpanded ? 'cust-row cust-row--expanded' : 'cust-row'}
                  >
                    <td className="cust-td">
                      <div className="cust-name-cell">
                        <div className="cust-avatar">
                          {c.business_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <span className="cust-name-text">{c.business_name}</span>
                          {c.engagement_mode === 'managed' && (
                            <span style={{
                              marginLeft: 8, fontSize: 10, fontWeight: 700,
                              color: 'var(--amber)', background: 'var(--amber-bg)',
                              border: '1px solid rgba(245,166,35,0.3)', borderRadius: 4,
                              padding: '1px 6px', verticalAlign: 'middle',
                            }}>
                              Managed
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="cust-td">
                      {c.email
                        ? <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="cust-email-link">{c.email}</a>
                        : <span className="cust-text-dim">—</span>}
                    </td>

                    <td className="cust-td">
                      {c.mobile
                        ? <a href={`tel:${c.mobile}`} onClick={e => e.stopPropagation()} className="cust-phone-link">{c.mobile}</a>
                        : <span className="cust-text-dim">—</span>}
                    </td>

                    <td className="cust-td">
                      {c.pan_number
                        ? <span className="cust-pan-badge">{c.pan_number}</span>
                        : <span className="cust-text-dim">—</span>}
                    </td>

                    <td className="cust-td">
                      {c.gstin
                        ? <span className="cust-gstin-text">{c.gstin}</span>
                        : <span className="cust-text-dim">—</span>}
                    </td>

                    <td className="cust-td-center">
                      <span className={c.tally_company_count > 0 ? 'cust-count-badge cust-count-badge--has-data' : 'cust-count-badge'}>
                        {c.tally_company_count ?? 0}
                      </span>
                    </td>

                    <td className="cust-td-center" onClick={e => { e.stopPropagation(); toggle(c.id); }}>
                      <span className={isExpanded ? 'cust-chevron cust-chevron--open' : 'cust-chevron'}>▾</span>
                    </td>
                  </tr>

                  {isExpanded && (
                    <ExpandedRow
                      customer={c}
                      allSources={allSources}
                      colSpan={COL_COUNT}
                      onCustomerUpdated={onCustomerUpdated}
                      onDelete={onDelete}
                      onEdit={() => setEditCustomer(c)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {editCustomer && (
        <EditCustomerModal
          customer={editCustomer}
          onClose={() => setEditCustomer(null)}
          onSaved={() => {
            setEditCustomer(null);
            onCustomerUpdated();
          }}
        />
      )}
    </>
  );
}

// =============================================================================
// Mobile Customer Cards (fallback for small screens)
// =============================================================================
function CustomerCardsMobile({ customers, allSources, onDelete, onCustomerUpdated }) {
  const [expandedId,   setExpandedId]   = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);

  return (
    <div className="cust-cards-list">
      {customers.map(c => {
        const isExpanded = expandedId === c.id;
        return (
          <div key={c.id} className="cust-mobile-card">
            <div onClick={() => setExpandedId(isExpanded ? null : c.id)} className="cust-mobile-card-header">
              <div className="cust-mobile-avatar">
                {c.business_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="cust-mobile-info">
                <div className="cust-mobile-name">{c.business_name}</div>
                <div className="cust-mobile-meta">
                  <span className="cust-text-mono">{c.pan_number}</span>
                  {c.email && <span>{c.email}</span>}
                  {c.mobile && <span className="cust-text-mono">{c.mobile}</span>}
                </div>
              </div>
              <div className="cust-mobile-count">
                <div className="cust-mobile-count-num">{c.tally_company_count ?? 0}</div>
                <div className="cust-mobile-count-label">cos</div>
              </div>
              <span className={isExpanded ? 'cust-chevron cust-chevron--open' : 'cust-chevron'}>▾</span>
            </div>
            {isExpanded && (
              <div className="cust-mobile-expanded">
                <ExpandedRow
                  customer={c} allSources={allSources} colSpan={1}
                  onCustomerUpdated={onCustomerUpdated}
                  onDelete={onDelete}
                  onEdit={() => setEditCustomer(c)}
                />
              </div>
            )}
          </div>
        );
      })}
      {editCustomer && (
        <EditCustomerModal customer={editCustomer} onClose={() => setEditCustomer(null)}
          onSaved={() => { setEditCustomer(null); onCustomerUpdated(); }} />
      )}
    </div>
  );
}

// =============================================================================
// Unlinked Sources Banner
// =============================================================================
function UnlinkedSourcesBanner({ sources, customers, onLinked }) {
  const [expanded, setExpanded] = useState(false);
  const [linking,  setLinking]  = useState(null);
  if (!sources || sources.length === 0) return null;
  return (
    <div className="cust-banner">
      <div onClick={() => setExpanded(e => !e)} className="cust-banner-header">
        <span className="cust-banner-icon">⚠</span>
        <div className="cust-banner-text">
          <div className="cust-banner-title">
            {sources.length} Tally {sources.length === 1 ? 'company' : 'companies'} not yet assigned to a customer
          </div>
          <div className="cust-banner-sub">
            Assign them for better organisation and billing tracking
          </div>
        </div>
        <span className={expanded ? 'cust-chevron cust-chevron--open' : 'cust-chevron'}>▾</span>
      </div>
      {expanded && (
        <div className="cust-banner-body">
          {sources.map(s => (
            <div key={s.id} className="cust-unlinked-item">
              <div className="cust-unlinked-info">
                <div className="cust-unlinked-name">{s.name}</div>
                {s.last_synced_at && <div className="cust-unlinked-sync">Last sync: {fmtDate(s.last_synced_at)}</div>}
              </div>
              <button onClick={() => setLinking(s)} className="cust-btn-assign">Assign →</button>
            </div>
          ))}
        </div>
      )}
      {linking && (
        <LinkSourceModal source={linking} availableSources={[]} customers={customers}
          onClose={() => setLinking(null)} onLinked={() => { setLinking(null); onLinked(); }} />
      )}
    </div>
  );
}

// =============================================================================
// CA Default Pages Panel
// =============================================================================
function CADefaultPagesPanel({ userRole }) {
  const [pages,   setPages]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');
  const [open,    setOpen]    = useState(false);

  useEffect(() => {
    fetch(`${BASE}/users/me`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setPages(d.customer_visible_pages ?? null))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await fetch(`${BASE}/customers/defaults/visible-pages`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({ pages }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setPages(data.customer_visible_pages ?? null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (!userRole || userRole.isAdmin && !userRole.isCA) return null;
  if (pages === undefined) return null;

  const isRestricted = pages !== null && pages.length < ALL_PAGE_IDS.length;

  return (
    <div className="cust-default-panel">
      <div onClick={() => setOpen(o => !o)} className="cust-default-panel-header">
        <span className="cust-banner-icon">🔒</span>
        <div className="cust-default-panel-info">
          <div className="cust-default-panel-title">Customer Page Visibility — Default Settings</div>
          <div className="cust-default-panel-desc">
            {isRestricted
              ? `Restricted — ${pages.length} of ${ALL_PAGE_IDS.length} pages visible to customers`
              : 'All pages visible to customers (no restriction)'}
          </div>
        </div>
        {isRestricted && <span className="cust-badge-restricted">Restricted</span>}
        <span className={open ? 'cust-chevron cust-chevron--open' : 'cust-chevron'}>▾</span>
      </div>

      {open && (
        <div className="cust-default-panel-body">
          <p className="cust-default-panel-desc-text">
            These defaults apply to all your customers unless you set a per-customer override in Edit Customer.
            Deselecting a page hides it from the customer's sidebar when they view their audit data.
          </p>
          <VisiblePagesEditor pages={pages} onChange={setPages} />
          <div className="cust-default-panel-footer">
            <button onClick={handleSave} disabled={saving} className="cust-btn-save-defaults">
              {saving ? 'Saving…' : 'Save Defaults'}
            </button>
            {pages !== null && (
              <button onClick={() => setPages(null)} className="cust-btn-reset-default">
                Reset — show all
              </button>
            )}
            {saved  && <span className="cust-status-saved">✓ Saved</span>}
            {error  && <span className="cust-status-error">{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function LicenseSummaryBar({ summary }) {
  if (!summary) return null;
  const { total_licenses, active_licenses, suspended_licenses, cancelled_licenses } = summary;
  const stats = [
    { label: 'Total Licenses', value: total_licenses,     mod: 'total'     },
    { label: 'Active',         value: active_licenses,    mod: 'active'    },
    { label: 'Suspended',      value: suspended_licenses, mod: 'suspended' },
    { label: 'Cancelled',      value: cancelled_licenses, mod: 'cancelled' },
  ];
  return (
    <div className="cust-license-bar">
      {stats.map(item => (
        <div key={item.label} className={`cust-license-stat cust-license-stat--${item.mod}`}>
          <div className="cust-license-value">{item.value ?? 0}</div>
          <div className="cust-license-label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Main Page
// =============================================================================
export default function Customers({ userRole }) {
  const isMobile = useIsMobile();
  const [customers,      setCustomers]      = useState([]);
  const [unlinked,       setUnlinked]       = useState([]);
  const [allSources,     setAllSources]     = useState([]);
  const [licenseSummary, setLicenseSummary] = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [showAdd,        setShowAdd]        = useState(false);
  const [search,         setSearch]         = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const headers = getAuthHeaders();
      const [custRes, unlinkedRes, licenseRes, sourcesRes] = await Promise.all([
        fetch(`${BASE}/customers`,                  { headers }),
        fetch(`${BASE}/customers/sources/unlinked`, { headers }),
        fetch(`${BASE}/customers/licenses/summary`, { headers }),
        fetch(`${BASE}/sources`,                    { headers }),
      ]);
      if (custRes.ok)     setCustomers(await custRes.json());
      if (unlinkedRes.ok) setUnlinked(await unlinkedRes.json());
      if (licenseRes.ok)  setLicenseSummary(await licenseRes.json());
      if (sourcesRes.ok)  setAllSources(await sourcesRes.json());
      if (!custRes.ok) {
        const d = await custRes.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to load customers');
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const silentRefresh = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [custRes, unlinkedRes, sourcesRes] = await Promise.all([
        fetch(`${BASE}/customers`,                  { headers }),
        fetch(`${BASE}/customers/sources/unlinked`, { headers }),
        fetch(`${BASE}/sources`,                    { headers }),
      ]);
      if (custRes.ok)     setCustomers(await custRes.json());
      if (unlinkedRes.ok) setUnlinked(await unlinkedRes.json());
      if (sourcesRes.ok)  setAllSources(await sourcesRes.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.business_name?.toLowerCase().includes(q) ||
      c.pan_number?.toLowerCase().includes(q) ||
      c.gstin?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.mobile?.includes(q) ||
      c.notes?.toLowerCase().includes(q)
    );
  });

  return (
    <PageWrap>
      <PageHeader
        title="Business Customers"
        sub="Manage your client list — link Tally companies to clients for organised audit tracking"
      />

      <LicenseSummaryBar summary={licenseSummary} />
      <CADefaultPagesPanel userRole={userRole} />
      <UnlinkedSourcesBanner sources={unlinked} customers={customers} onLinked={silentRefresh} />

      <div className="cust-toolbar">
        <input
          type="text" placeholder="Search by name, PAN, GSTIN, email, phone…"
          value={search} onChange={e => setSearch(e.target.value)}
          className="cust-search"
        />
        <button onClick={load} className="cust-btn-refresh">↺ Refresh</button>
        <button onClick={() => setShowAdd(true)} className="cust-btn-add">+ Add Customer</button>
      </div>

      {error && <div className="cust-error-banner">{error}</div>}

      {loading ? (
        <Card><div className="cust-loading-wrap"><Skeleton rows={5} /></div></Card>
      ) : filtered.length === 0 ? (
        <div className="cust-empty-state">
          <div className="cust-empty-icon">🏢</div>
          {search ? (
            <>
              <div className="cust-empty-title">No customers match "{search}"</div>
              <div className="cust-empty-no-match">Try a different name, PAN or email</div>
            </>
          ) : (
            <>
              <div className="cust-empty-title">No customers yet</div>
              <div className="cust-empty-sub">Add your first business customer to start linking Tally companies and tracking licenses</div>
              <button onClick={() => setShowAdd(true)} className="cust-btn-add-first">+ Add First Customer</button>
            </>
          )}
        </div>
      ) : (
        <Card>
          <div className="cust-list-header">
            {filtered.length} {filtered.length === 1 ? 'customer' : 'customers'}{search && ` matching "${search}"`}
          </div>
          {isMobile ? (
            <div className="cust-list-mobile-wrap">
              <CustomerCardsMobile
                customers={filtered} allSources={allSources}
                onDelete={id => setCustomers(prev => prev.filter(c => c.id !== id))}
                onCustomerUpdated={silentRefresh}
              />
            </div>
          ) : (
            <CustomerTable
              customers={filtered} allSources={allSources}
              onDelete={id => setCustomers(prev => prev.filter(c => c.id !== id))}
              onCustomerUpdated={silentRefresh}
            />
          )}
        </Card>
      )}

      {showAdd && (
        <AddCustomerModal
          onClose={() => setShowAdd(false)}
          onSaved={c => { setCustomers(prev => [c, ...prev]); setShowAdd(false); }}
        />
      )}
    </PageWrap>
  );
}
